// Agenda geral (tempo × médicos) + agenda de um médico (API real).
// `day` → /clinica/agenda?date=. `forDoctor` → /clinica/doctors/:id/agenda.
import AXIOS from "@/services/axios";
import { initialsOf } from "./helpers";

// Linhas fixas da grade (horários) — fidelidade ao handoff (grade horária).
const TIME_ROWS = [
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "14:00",
  "15:00",
  "16:00"
];

// Backend usa status em inglês; a grade mapeia por chaves em PT (STATUS_CLASS).
const STATUS_MAP = { confirmed: "confirmada", pending: "agendada" };

function adaptDay(payload, params) {
  let columns = (payload.doctors || []).map(d => ({
    id: d.id,
    name: d.name,
    initials: initialsOf(d.name),
    specialty: d.specialty || "",
    specialtyShort: d.specialty || ""
  }));

  if (params.specialty && params.specialty !== "todas") {
    columns = columns.filter(c => c.specialty === params.specialty);
  }

  const appointments = (payload.appointments || []).map(a => ({
    id: a.id,
    doctorId: a.doctor_id,
    doctorName: a.doctor_name,
    patientName: a.patient && a.patient.name,
    time: a.time,
    status: STATUS_MAP[a.status] || "agendada"
  }));

  // DT-1 resolvido: a grade fixa de horas cheias escondia consultas fora
  // delas (ex.: 09:30). Linhas = esqueleto do handoff ∪ horários reais.
  const times = appointments.map(a => a.time).filter(Boolean);
  const rows = Array.from(new Set([...TIME_ROWS, ...times])).sort();

  return { columns, rows, appointments };
}

export class ClinicAgenda {
  /**
   * Agenda geral do dia: colunas = médicos com consulta no dia (filtráveis por
   * especialidade), linhas = horários, células = consultas.
   */
  static day(params = {}) {
    const query = params.date ? `?date=${params.date}` : "";
    return AXIOS.get(`clinica/agenda${query}`).then(res => ({
      data: adaptDay(res.data, params)
    }));
  }

  /** Horários disponíveis de um médico (mesmo endpoint do fluxo do paciente;
   *  p/ staff o backend escopa pelas unidades da clínica). */
  static doctorSlots(doctorId) {
    return AXIOS.get(`slots/doctors/${doctorId}/availables`);
  }

  /** Detalhe de uma consulta (modal da agenda): observação + contato do
   *  paciente. Endpoint canônico GET /admin/appointments/:id (clinic_staff). */
  static appointment(id) {
    return AXIOS.get(`admin/appointments/${id}`).then(({ data }) => ({
      data: {
        id: data.id,
        patientId: data.user_id,
        observation: data.observation || "",
        patientEmail: data.patient && data.patient.email,
        patientPhone: data.patient && data.patient.phone_number
      }
    }));
  }

  /** Cria consulta em nome de um paciente (staff da clínica).
   *  Endpoint canônico POST /admin/appointments — policy clinic_staff;
   *  o backend gera order 'paid' zerada e notifica médico/paciente. */
  static createAppointment({ patientId, slotId, scheduledAt, observation }) {
    return AXIOS.post("admin/appointments", {
      appointment: {
        user_id: patientId,
        slot_id: slotId,
        scheduled_at: scheduledAt,
        observation: observation
      }
    });
  }

  /** Agenda semanal de um médico (consultas dele nas unidades da clínica). */
  static forDoctor(id) {
    return AXIOS.get(`clinica/doctors/${id}/agenda`).then(({ data }) => ({
      data: {
        doctor: data.doctor || { name: "" },
        appointments: (data.appointments || []).map(a => ({
          id: a.id,
          datetime: a.datetime,
          patientId: a.patient && a.patient.id,
          patientName: a.patient && a.patient.name,
          status: STATUS_MAP[a.status] || "agendada"
        }))
      }
    }));
  }
}

export default ClinicAgenda;
